import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Notification, { NotificationContainer } from '../../../components/Notification';
import tprmService from '../../../services/compliance/tprm.service';
import {
  TprmDashboardResponse,
  TprmDashboardVendorRow,
} from '../../../types/compliance';

const TIER_COLORS: Record<string, string> = {
  low: '#22c55e',
  medium: '#3b82f6',
  high: '#f59e0b',
  critical: '#ef4444',
};

const TIER_BADGE: Record<string, string> = {
  low: 'bg-green-200 text-green-800 dark:bg-green-900 dark:text-green-200',
  medium: 'bg-blue-200 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  high: 'bg-amber-200 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  critical: 'bg-red-200 text-red-800 dark:bg-red-900 dark:text-red-200',
};

const STATUS_BADGE: Record<string, string> = {
  draft: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  submitted: 'bg-blue-200 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  under_review: 'bg-indigo-200 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
  approved: 'bg-green-200 text-green-800 dark:bg-green-900 dark:text-green-200',
  approved_with_conditions: 'bg-amber-200 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  rejected: 'bg-red-200 text-red-800 dark:bg-red-900 dark:text-red-200',
  archived: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
};

const DOMAIN_LABELS: Record<string, string> = {
  data_sensitivity: 'Data Sensitivity',
  system_access: 'System / Access Risk',
  operational: 'Operational Criticality',
  security: 'Security Maturity',
  financial: 'Financial / Contractual',
  compliance: 'Compliance / Regulatory',
  business_context: 'Business Context',
};

const POSTURE_BADGE: Record<string, string> = {
  Strong: 'bg-green-200 text-green-800 dark:bg-green-900 dark:text-green-200',
  Moderate: 'bg-blue-200 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  'Elevated Risk': 'bg-amber-200 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  'Critical Attention': 'bg-red-200 text-red-800 dark:bg-red-900 dark:text-red-200',
};

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 ${className}`}
    >
      {children}
    </div>
  );
}

function KpiCard({
  label,
  value,
  accent,
  pill,
  accentBar,
}: {
  label: string;
  value: React.ReactNode;
  accent?: string;
  pill?: { label: string; className: string } | null;
  accentBar?: string;
}) {
  return (
    <Card className="flex flex-col">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted text-center">
        {label}
      </div>
      <div
        className={`text-3xl font-bold text-center mt-2 ${accent || 'text-body'}`}
      >
        {value}
      </div>
      {pill && (
        <div className="flex justify-center mt-1">
          <span className={`px-2 py-0.5 text-xs font-medium rounded ${pill.className}`}>
            {pill.label}
          </span>
        </div>
      )}
      {accentBar && <div className={`h-1 mt-3 rounded ${accentBar}`} />}
    </Card>
  );
}

function CalloutCard({
  value,
  title,
  subtitle,
}: {
  value: number;
  title: string;
  subtitle: string;
}) {
  return (
    <Card>
      <div className="flex items-center gap-4">
        <div className="text-3xl font-bold text-body">{value}</div>
        <div>
          <div className="text-sm font-semibold text-body">{title}</div>
          <div className="text-xs text-muted">{subtitle}</div>
        </div>
      </div>
    </Card>
  );
}

function BoolIcon({ value }: { value: boolean }) {
  return value ? (
    <CheckIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
  ) : (
    <XMarkIcon className="w-4 h-4 text-red-600 dark:text-red-400" />
  );
}

type Props = {
  embedded?: boolean;
};


export default function TprmDashboard({ embedded = false }: Props = {}) {
  const [isDark, setIsDark] = useState<boolean>(() => document.documentElement.classList.contains('dark'));
  useEffect(() => {
    const check = () => setIsDark(document.documentElement.classList.contains('dark'));
    check();
    const observer = new MutationObserver(() => check());
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  const [dashboard, setDashboard] = useState<TprmDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorNotification, setErrorNotification] =
    useState<{ title: string; message: string } | null>(null);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const data = await tprmService.dashboard();
      setDashboard(data);
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error occurred';
      setErrorNotification({ title: 'Failed to load dashboard', message: msg });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const tierPieData = useMemo(() => {
    if (!dashboard) return [];
    return Object.entries(dashboard.series.risk_tier_distribution)
      .filter(([, v]) => v > 0)
      .map(([k, v]) => ({ name: k, value: v, color: TIER_COLORS[k] || '#94a3b8' }));
  }, [dashboard]);

  const domainBarData = useMemo(() => {
    if (!dashboard) return [];
    return Object.keys(DOMAIN_LABELS).map((k) => ({
      name: DOMAIN_LABELS[k],
      score: dashboard.series.avg_score_by_domain[k] ?? 0,
    }));
  }, [dashboard]);

  const deptBarData = useMemo(() => {
    if (!dashboard) return [];
    return Object.entries(dashboard.series.vendors_by_department).map(([k, v]) => ({
      name: k,
      value: v,
    }));
  }, [dashboard]);

  const svcPieData = useMemo(() => {
    if (!dashboard) return [];
    const SVC_COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#a855f7', '#06b6d4', '#64748b', '#f97316'];
    return Object.entries(dashboard.series.vendors_by_service_type)
      .filter(([, v]) => v > 0)
      .map(([k, v], i) => ({ name: k, value: v, color: SVC_COLORS[i % SVC_COLORS.length] }));
  }, [dashboard]);

  const renderVendorLink = (v: TprmDashboardVendorRow) => (
    <Link
      to={`/compliance/tprm/vendors/${v.id}`}
      className="font-semibold text-blue-600 hover:text-blue-800 dark:text-blue-400"
    >
      {v.name}
    </Link>
  );

  return (
    <div className="h-full w-full p-6 pb-24">
      {/* Header */}
      {!embedded && (
        <div className="mb-6">
          <h1 className="text-2xl font-bold">3rd Party Risk Management</h1>
          <p className="text-sm text-muted">GWII Vendor Risk Register</p>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-24 text-gray-500">
          Loading dashboard...
        </div>
      )}

      {dashboard && !loading && (
        <>
          {/* Row 1: 4 KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <KpiCard
              label="Total Vendors"
              value={dashboard.kpis.total_vendors}
              accentBar="bg-blue-500"
            />
            <KpiCard
              label="Avg Risk Score"
              value={
                dashboard.kpis.avg_risk_score != null
                  ? dashboard.kpis.avg_risk_score.toFixed(1)
                  : '—'
              }
              accent="text-green-600 dark:text-green-400"
              pill={
                dashboard.kpis.avg_risk_posture
                  ? {
                      label: dashboard.kpis.avg_risk_posture,
                      className:
                        POSTURE_BADGE[dashboard.kpis.avg_risk_posture] ||
                        'bg-gray-200 text-gray-800',
                    }
                  : null
              }
              accentBar="bg-blue-500"
            />
            <KpiCard
              label="Open Reviews"
              value={dashboard.kpis.open_reviews}
              accentBar="bg-amber-500"
            />
            <Link to="/compliance/tprm?has_pii=true">
              <KpiCard
                label="Vendors with PII"
                value={dashboard.kpis.vendors_with_pii}
                accentBar="bg-red-500"
              />
            </Link>
          </div>

          {/* Row 2: 4 Tier KPIs — each drills into the register pre-filtered */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Link to="/compliance/tprm?risk_tier=low">
              <KpiCard
                label=""
                value={dashboard.kpis.low_risk}
                accent="text-green-600 dark:text-green-400"
                pill={{ label: 'Low Risk', className: TIER_BADGE.low }}
                accentBar="bg-green-500"
              />
            </Link>
            <Link to="/compliance/tprm?risk_tier=medium">
              <KpiCard
                label=""
                value={dashboard.kpis.medium_risk}
                accent="text-amber-600 dark:text-amber-400"
                pill={{ label: 'Medium Risk', className: TIER_BADGE.medium }}
                accentBar="bg-amber-500"
              />
            </Link>
            <Link to="/compliance/tprm?risk_tier=high">
              <KpiCard
                label=""
                value={dashboard.kpis.high_risk}
                accent="text-orange-600 dark:text-orange-400"
                pill={{ label: 'High Risk', className: TIER_BADGE.high }}
                accentBar="bg-orange-500"
              />
            </Link>
            <Link to="/compliance/tprm?risk_tier=critical">
              <KpiCard
                label=""
                value={dashboard.kpis.critical_risk}
                accent="text-red-600 dark:text-red-400"
                pill={{ label: 'Critical', className: TIER_BADGE.critical }}
                accentBar="bg-red-500"
              />
            </Link>
          </div>

          {/* Callouts row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <CalloutCard
              value={dashboard.kpis.approved_without_mfa}
              title="Approved Vendors Without MFA"
              subtitle="Review access controls"
            />
            <Link to="/compliance/tprm?is_critical_operations=true">
              <CalloutCard
                value={dashboard.kpis.critical_operations}
                title="Critical Operations Vendors"
                subtitle="Single point of failure or dispatch"
              />
            </Link>
            <Link to="/compliance/tprm?overdue_review_only=true">
              <CalloutCard
                value={dashboard.kpis.overdue_reviews}
                title="Overdue Reviews"
                subtitle="Past next review date"
              />
            </Link>
            <CalloutCard
              value={dashboard.kpis.vendors_with_open_incidents ?? 0}
              title="Vendors With Open Incidents"
              subtitle="Open / investigating / contained"
            />
          </div>

          {/* Row: Tier distribution + Domain scores */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <Card>
              <h3 className="text-sm font-semibold mb-3">Risk Tier Distribution</h3>
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={tierPieData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={1}
                    >
                      {tierPieData.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card>
              <h3 className="text-sm font-semibold mb-3">Average Score by Risk Domain</h3>
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <BarChart data={domainBarData} layout="vertical">
                    <XAxis type="number" domain={[0, 100]} tick={{ fill: isDark ? '#9ca3af' : '#4b5563' }} />
                    <YAxis type="category" dataKey="name" width={170} tick={{ fontSize: 11, fill: isDark ? '#9ca3af' : '#4b5563' }} />
                    <Tooltip />
                    <Bar dataKey="score" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          {/* Row: Dept + Service Type */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <Card>
              <h3 className="text-sm font-semibold mb-3">Vendors by Department</h3>
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <BarChart data={deptBarData}>
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: isDark ? '#9ca3af' : '#4b5563' }} />
                    <YAxis allowDecimals={false} tick={{ fill: isDark ? '#9ca3af' : '#4b5563' }} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#22d3ee" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card>
              <h3 className="text-sm font-semibold mb-3">Vendors by Service Type</h3>
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={svcPieData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={1}
                    >
                      {svcPieData.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          {/* Highest Risk Vendors */}
          <Card className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold">Highest Risk Vendors</h3>
              <Link
                to="/compliance/tprm?tab=data"
                className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-highlight"
              >
                View All
              </Link>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-muted border-b border-gray-200 dark:border-gray-700">
                    <th className="py-2 pr-3">Vendor</th>
                    <th className="py-2 pr-3">Service Type</th>
                    <th className="py-2 pr-3">Dept</th>
                    <th className="py-2 pr-3">Score</th>
                    <th className="py-2 pr-3">Tier</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">PII</th>
                    <th className="py-2 pr-3">MFA</th>
                    <th className="py-2 pr-3">SSO</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard.highest_risk_vendors.map((v) => (
                    <tr key={v.id} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-2 pr-3">{renderVendorLink(v)}</td>
                      <td className="py-2 pr-3">{v.service_type || '—'}</td>
                      <td className="py-2 pr-3">{v.department || '—'}</td>
                      <td className="py-2 pr-3">
                        {v.total_score != null ? v.total_score.toFixed(2) : '—'}
                      </td>
                      <td className="py-2 pr-3">
                        {v.risk_tier ? (
                          <span
                            className={`px-2 py-0.5 text-xs font-medium rounded ${TIER_BADGE[v.risk_tier] || ''}`}
                          >
                            {v.risk_tier}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        <span
                          className={`px-2 py-0.5 text-xs font-medium rounded ${STATUS_BADGE[v.status] || ''}`}
                        >
                          {v.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="py-2 pr-3"><BoolIcon value={v.has_pii} /></td>
                      <td className="py-2 pr-3"><BoolIcon value={v.mfa_supported} /></td>
                      <td className="py-2 pr-3"><BoolIcon value={v.sso_supported} /></td>
                    </tr>
                  ))}
                  {dashboard.highest_risk_vendors.length === 0 && (
                    <tr>
                      <td className="py-4 text-gray-500" colSpan={9}>
                        No vendors yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Recently Added Vendors */}
          <Card>
            <h3 className="text-sm font-semibold mb-2">Recently Added Vendors</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-muted border-b border-gray-200 dark:border-gray-700">
                    <th className="py-2 pr-3">Vendor</th>
                    <th className="py-2 pr-3">Dept</th>
                    <th className="py-2 pr-3">Service Type</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Added</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard.recently_added_vendors.map((v) => (
                    <tr key={v.id} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-2 pr-3">{renderVendorLink(v)}</td>
                      <td className="py-2 pr-3">{v.department || '—'}</td>
                      <td className="py-2 pr-3">{v.service_type || '—'}</td>
                      <td className="py-2 pr-3">
                        <span
                          className={`px-2 py-0.5 text-xs font-medium rounded ${STATUS_BADGE[v.status] || ''}`}
                        >
                          {v.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="py-2 pr-3">
                        {v.created_at ? new Date(v.created_at).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                  {dashboard.recently_added_vendors.length === 0 && (
                    <tr>
                      <td className="py-4 text-gray-500" colSpan={5}>
                        No vendors yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {errorNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="error"
            title={errorNotification.title}
            message={errorNotification.message}
            showClose={true}
            onClose={() => setErrorNotification(null)}
            autoDismiss={5000}
          />
        </NotificationContainer>
      )}
    </div>
  );
}
